import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PackageOpen,
  Package,
  AlertCircle,
  RefreshCw,
  Loader,
  Trash2,
  CheckCheck,
  CheckCircle,
  FileScan
} from 'lucide-react';
import { pOrdersService } from '../../../../services/pOrdersService';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrdersDto, POrderLineDto, POrderFinalizeBlocker } from '../../../../types/porders';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { DocInterpretationView } from './doc-interpretation/DocInterpretationView';
import { useDocInterpretationAvailability } from '../../../../hooks/useDocInterpretationAvailability';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, SummaryCalculation } from '../../../shared/table';

// Table row type for working orders
interface WorkingOrderTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  statusText: string;
  orderDate: string | null;
  deliveryDate: string | null;
  supplierNo: string;
  supplierName: string | null;
  yourRef: string | null;
  ourRef: string | null;
  warehouse: string | null;
  projectNo: string | null;
  depNo: string | null;
  // Extra fields (hidden by default)
  supplierOrderNo: string | null;
  carrierName: string | null;
  contactName: string | null;
  eMailAddress: string | null;
  delCustomerName: string | null;
  delAddress1: string | null;
  delZipCode: string | null;
  delCity: string | null;
  deliveryMethod: string | null;
  numOfLines: number | null;
  created: string | null;
  lastUpdate: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  orderType: string | null;
  orderStatusERP: string | null;
  delAddressNo: number | null;
  delAddress2: string | null;
  delAddress3: string | null;
  delCountryCode: string | null;
  deliveryGLN: string | null;
  temsOfDelivery: string | null;
  shipmentType: string | null;
  orderFreeField1: string | null;
  orderFreeField2: string | null;
  orderFreeField3: string | null;
  // Internal
  _original: POrdersDto;
  _lines?: POrderLineDto[];
  _linesLoaded?: boolean;
}

// Create column factory
const columnFactory = createColumnFactory<WorkingOrderTableRow>();

interface WorkingOrdersProps {
  onBack?: () => void;
}

const WorkingOrders: React.FC<WorkingOrdersProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [orders, setOrders] = useState<POrdersDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderLines, setOrderLines] = useState<Map<number, POrderLineDto[]>>(new Map());
  const [loadingLines, setLoadingLines] = useState<Set<number>>(new Set());
  const [selectedOrderData, setSelectedOrderData] = useState<WorkingOrderTableRow[]>([]);
  // «Les inn leverandørdokument» (dokumenttolking, TW 36423840): bestillingene som var valgt da bildet ble åpnet.
  const [docInterpretationOrders, setDocInterpretationOrders] = useState<WorkingOrderTableRow[] | null>(null);
  // Knappen vises bare når tenanten har tabellene (migrasjon 127) og funksjonen er aktivert
  const docInterpretation = useDocInterpretationAvailability();
  const showDocInterpretation = docInterpretation.installed && docInterpretation.enabled;
  const [deleteConfirm, setDeleteConfirm] = useState<{ orders: { orderNo: number; orderNoERP: string; numOfLines: number | null }[] } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ current: number; total: number; failed: string[] } | null>(null);
  // «Avslutt og eksporter kvittering» (TW 36402619)
  const [finalizeConfirm, setFinalizeConfirm] = useState<{ orderNo: number; orderNoERP: string; orderStatus: number }[] | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeBlocked, setFinalizeBlocked] = useState<{ message: string; lines: POrderFinalizeBlocker[] } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pOrdersService.getOrdersForReceiving(currentWarehouse || undefined);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.workingOrders.errors.loadFailed', {}, 'Feil ved lasting av bestillinger'));
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const loadOrderLines = useCallback(async (orderNo: number) => {
    if (orderLines.has(orderNo)) return;

    try {
      setLoadingLines(prev => new Set(prev).add(orderNo));
      // includeCancelled: lagersjef-bildet viser kansellerte linjer (LineStatus 80)
      // med merking og gjenåpne-mulighet — mottaksbildene skjuler dem.
      const lines = await pOrderLineService.getOrderLines(orderNo, true);
      setOrderLines(prev => new Map(prev).set(orderNo, lines));
    } catch (err) {
      console.error(`Error loading lines for order ${orderNo}:`, err);
    } finally {
      setLoadingLines(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  }, [orderLines]);

  // Selection change handler
  const handleSelectionChange = useCallback((_selectedRows: (string | number)[], selectedData: WorkingOrderTableRow[]) => {
    setSelectedOrderData(selectedData);
  }, []);

  // Delete orders handler (supports single and bulk)
  const handleDeleteOrders = useCallback(async () => {
    if (!deleteConfirm) return;
    const { orders: ordersToDelete } = deleteConfirm;
    setDeleting(true);
    setDeleteProgress({ current: 0, total: ordersToDelete.length, failed: [] });

    const failed: string[] = [];
    for (let i = 0; i < ordersToDelete.length; i++) {
      const order = ordersToDelete[i];
      setDeleteProgress({ current: i + 1, total: ordersToDelete.length, failed });
      try {
        await pOrdersService.deleteOrder(order.orderNo);
      } catch (err) {
        failed.push(`${order.orderNoERP}: ${err instanceof Error ? err.message : t('manager.modules.workingOrders.errors.unknownError', {}, 'Ukjent feil')}`);
      }
    }

    setDeleteConfirm(null);
    setDeleteProgress(null);
    setSelectedOrderData([]);
    setDeleting(false);

    if (failed.length > 0) {
      setError(t('manager.modules.workingOrders.errors.deleteFailed', { '0': failed.length, '1': ordersToDelete.length, '2': failed.join('\n') }, `Kunne ikke slette ${failed.length} av ${ordersToDelete.length} bestillinger:\n${failed.join('\n')}`));
    }

    await loadOrders();
  }, [deleteConfirm, loadOrders, t]);

  // Avslutt og eksporter kvittering: alt-eller-ingenting. Stoppes backend av mottak som ikke er
  // eksportert — da vises linjene og ingenting er endret; mottaket fullføres på vanlig måte.
  const handleFinalizeOrders = useCallback(async () => {
    if (!finalizeConfirm) return;
    setFinalizing(true);
    try {
      const result = await pOrdersService.finalizeOrders(finalizeConfirm.map(o => o.orderNo));
      setFinalizeConfirm(null);
      if (result.success) {
        setSelectedOrderData([]);
        setSuccessMessage(t('manager.modules.workingOrders.finalize.success', { '0': result.finalizedOrders.length }, `${result.finalizedOrders.length} bestilling(er) avsluttet og kvittering sendt til ERP.`));
        await loadOrders();
      } else {
        setFinalizeBlocked({
          message: result.blockingLines.length > 0
            ? t('manager.modules.workingOrders.finalize.blockedMessage', {}, 'Avslutningen er stoppet fordi det finnes mottak som ikke er eksportert til ERP. Fullfør mottaket på vanlig måte og prøv igjen. Ingenting er endret.')
            : (result.errorMessage || t('manager.modules.workingOrders.finalize.failed', {}, 'Kunne ikke avslutte bestillingen')),
          lines: result.blockingLines
        });
      }
    } catch (err) {
      setFinalizeConfirm(null);
      setFinalizeBlocked({
        message: err instanceof Error ? err.message : t('manager.modules.workingOrders.finalize.failed', {}, 'Kunne ikke avslutte bestillingen'),
        lines: []
      });
    } finally {
      setFinalizing(false);
    }
  }, [finalizeConfirm, loadOrders, t]);

  // Transform orders to table data
  const tableData: WorkingOrderTableRow[] = useMemo(() => {
    return orders.map(order => ({
      id: order.orderNo,
      orderNo: order.orderNo,
      orderNoERP: order.orderNoERP || order.orderNo.toString(),
      orderStatus: order.orderStatus,
      statusText: order.statusText || '',
      orderDate: order.orderDate ? new Date(order.orderDate).toISOString() : null,
      deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString() : null,
      supplierNo: order.supplierNo,
      supplierName: order.supplierName || null,
      yourRef: order.yourRef || null,
      ourRef: order.ourRef || null,
      warehouse: order.warehouse || null,
      projectNo: order.projectNo || null,
      depNo: order.depNo || null,
      // Extra fields
      supplierOrderNo: order.supplierOrderNo || null,
      carrierName: order.carrierName || null,
      contactName: order.contactName || null,
      eMailAddress: order.eMailAddress || null,
      delCustomerName: order.delCustomerName || null,
      delAddress1: order.delAddress1 || null,
      delZipCode: order.delZipCode || null,
      delCity: order.delCity || null,
      deliveryMethod: order.deliveryMethod || null,
      numOfLines: order.numOfLines ?? null,
      created: order.created ? new Date(order.created).toISOString() : null,
      lastUpdate: order.lastUpdate ? new Date(order.lastUpdate).toISOString() : null,
      createdBy: order.createdBy || null,
      updatedBy: order.updatedBy || null,
      orderType: order.orderType || null,
      orderStatusERP: order.orderStatusERP || null,
      delAddressNo: order.delAddressNo ?? null,
      delAddress2: order.delAddress2 || null,
      delAddress3: order.delAddress3 || null,
      delCountryCode: order.delCountryCode || null,
      deliveryGLN: order.deliveryGLN || null,
      temsOfDelivery: order.temsOfDelivery || null,
      shipmentType: order.shipmentType || null,
      orderFreeField1: order.orderFreeField1 || null,
      orderFreeField2: order.orderFreeField2 || null,
      orderFreeField3: order.orderFreeField3 || null,
      _original: order,
      _lines: orderLines.get(order.orderNo),
      _linesLoaded: orderLines.has(order.orderNo)
    }));
  }, [orders, orderLines]);

  // Row ID function
  const getRowId = useCallback((row: WorkingOrderTableRow) => row.orderNo, []);


  // Kode-baserte, språk-bevisste filteropsjoner for status-kolonnen (StatusType 1).
  const orderStatusOptions = useStatusOptions(1);

  // Column definitions
  const columns: ColumnDefinition<WorkingOrderTableRow>[] = useMemo(() => [
    // === SYNLIGE HOVEDFELTER ===
    columnFactory.createNumberColumn('orderNo', 'orderNo', t('manager.modules.workingOrders.columns.orderNo', {}, 'Ordrenr'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null && value !== undefined ? value.toString() : '-'
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.workingOrders.columns.orderNoERP', {}, 'ERP Ordrenr'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', t('manager.modules.workingOrders.columns.projectNo', {}, 'Prosjektnr'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('depNo', 'depNo', t('manager.modules.workingOrders.columns.depNo', {}, 'Avdeling'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('supplierName', 'supplierName', t('manager.modules.workingOrders.columns.supplierName', {}, 'Leverandør'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createDateColumn('orderDate', 'orderDate', t('manager.modules.workingOrders.columns.orderDate', {}, 'Ordredato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', t('manager.modules.workingOrders.columns.deliveryDate', {}, 'Leveringsdato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('yourRef', 'yourRef', t('manager.modules.workingOrders.columns.yourRef', {}, 'Deres ref'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('ourRef', 'ourRef', t('manager.modules.workingOrders.columns.ourRef', {}, 'Vår ref'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('warehouse', 'warehouse', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('orderStatus', 'orderStatus', t('manager.modules.workingOrders.columns.orderStatus', {}, 'Ordrestatus'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: orderStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.orderStatus} statusType={1} />
    }),

    // === SKJULTE EKSTRAFELTER ===
    columnFactory.createTextColumn('orderType', 'orderType', t('manager.modules.workingOrders.columns.orderType', {}, 'Ordretype'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('orderStatusERP', 'orderStatusERP', t('manager.modules.workingOrders.columns.orderStatusERP', {}, 'ERP-status'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('supplierNo', 'supplierNo', t('manager.modules.workingOrders.columns.supplierNo', {}, 'Leverandørnr'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('supplierOrderNo', 'supplierOrderNo', t('manager.modules.workingOrders.columns.supplierOrderNo', {}, 'Lev. ordrenr'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', t('warehouse.general.carrier', {}, 'Transportør'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('contactName', 'contactName', t('manager.modules.workingOrders.columns.contactName', {}, 'Kontaktperson'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('eMailAddress', 'eMailAddress', t('manager.modules.workingOrders.columns.eMailAddress', {}, 'E-post'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delCustomerName', 'delCustomerName', t('manager.modules.workingOrders.columns.delCustomerName', {}, 'Lev. kundenavn'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createNumberColumn('delAddressNo', 'delAddressNo', t('manager.modules.workingOrders.columns.delAddressNo', {}, 'Lev. adressenr'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null && value !== undefined ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress1', 'delAddress1', t('manager.modules.workingOrders.columns.delAddress1', {}, 'Lev. adresse'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress2', 'delAddress2', t('manager.modules.workingOrders.columns.delAddress2', {}, 'Lev. adresse 2'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress3', 'delAddress3', t('manager.modules.workingOrders.columns.delAddress3', {}, 'Lev. adresse 3'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delZipCode', 'delZipCode', t('manager.modules.workingOrders.columns.delZipCode', {}, 'Lev. postnr'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delCity', 'delCity', t('manager.modules.workingOrders.columns.delCity', {}, 'Lev. poststed'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryCode', 'delCountryCode', t('manager.modules.workingOrders.columns.delCountryCode', {}, 'Lev. landkode'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryGLN', 'deliveryGLN', t('manager.modules.workingOrders.columns.deliveryGLN', {}, 'Lev. GLN'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryMethod', 'deliveryMethod', t('manager.modules.workingOrders.columns.deliveryMethod', {}, 'Leveringsmåte'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('temsOfDelivery', 'temsOfDelivery', t('manager.modules.workingOrders.columns.temsOfDelivery', {}, 'Leveringsbetingelser'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('shipmentType', 'shipmentType', t('manager.modules.workingOrders.columns.shipmentType', {}, 'Forsendelsestype'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField1', 'orderFreeField1', t('manager.modules.workingOrders.columns.orderFreeField1', {}, 'Fritekst 1'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField2', 'orderFreeField2', t('manager.modules.workingOrders.columns.orderFreeField2', {}, 'Fritekst 2'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('orderFreeField3', 'orderFreeField3', t('manager.modules.workingOrders.columns.orderFreeField3', {}, 'Fritekst 3'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createNumberColumn('numOfLines', 'numOfLines', t('manager.modules.workingOrders.columns.numOfLines', {}, 'Ant. linjer'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      // Synlig som standard — summeringsraden viser sum linjer under denne kolonnen.
      visible: true
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.workingOrders.columns.created', {}, 'Opprettet'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.workingOrders.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.workingOrders.columns.createdBy', {}, 'Opprettet av'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.workingOrders.columns.updatedBy', {}, 'Oppdatert av'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    })
  ], [orderStatusOptions, t]);

  // Summeringsrad i bunnen: antall bestillinger og sum linjer over synlige (filtrerte) rader.
  // Beregnes av WarehouseTable over processedData — samme lest som «Plukklister under arbeid».
  const summaryCalculations: SummaryCalculation<WorkingOrderTableRow>[] = useMemo(() => [
    { column: 'orderNo' as keyof WorkingOrderTableRow, type: 'count' as const },
    { column: 'numOfLines' as keyof WorkingOrderTableRow, type: 'sum' as const }
  ], []);

  // Kanseller/gjenåpne ordrelinje (LineStatus 80). Etterpå: tøm linje-cache for ordren
  // og last både linjer og ordreliste på nytt (ordrestatus kan ha endret seg).
  const handleCancelLine = useCallback(async (orderNo: number, lineNo: number) => {
    if (!window.confirm(t('manager.modules.workingOrders.lines.confirmCancel', {}, 'Kansellere denne ordrelinjen? Linjen skjules for mottak, men kan gjenåpnes.'))) {
      return;
    }
    try {
      await pOrderLineService.cancelOrderLine(orderNo, lineNo);
      setOrderLines(prev => {
        const next = new Map(prev);
        next.delete(orderNo);
        return next;
      });
      const lines = await pOrderLineService.getOrderLines(orderNo, true);
      setOrderLines(prev => new Map(prev).set(orderNo, lines));
      loadOrders();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t('manager.modules.workingOrders.lines.cancelFailed', {}, 'Kunne ikke kansellere ordrelinjen'));
    }
  }, [t, loadOrders]);

  const handleReopenLine = useCallback(async (orderNo: number, lineNo: number) => {
    try {
      await pOrderLineService.reopenOrderLine(orderNo, lineNo);
      setOrderLines(prev => {
        const next = new Map(prev);
        next.delete(orderNo);
        return next;
      });
      const lines = await pOrderLineService.getOrderLines(orderNo, true);
      setOrderLines(prev => new Map(prev).set(orderNo, lines));
      loadOrders();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t('manager.modules.workingOrders.lines.reopenFailed', {}, 'Kunne ikke gjenåpne ordrelinjen'));
    }
  }, [t, loadOrders]);

  // Render order lines detail (expandable row content)
  const renderOrderLines = useCallback((order: WorkingOrderTableRow) => {
    const lines = orderLines.get(order.orderNo);
    const isLoadingLinesForOrder = loadingLines.has(order.orderNo);

    if (isLoadingLinesForOrder) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">{t('manager.modules.workingOrders.lines.loading', {}, 'Laster linjer...')}</span>
        </div>
      );
    }

    if (!lines) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          {t('manager.modules.workingOrders.lines.loadFailed', {}, 'Kunne ikke laste linjer')}
        </div>
      );
    }

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>{t('manager.modules.workingOrders.lines.title', { '0': lines.length }, `Ordrelinjer (${lines.length})`)}</span>
        </h4>

        {lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.workingOrders.lines.columns.lineNo', {}, 'Linjenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.workingOrders.lines.columns.itemNo', {}, 'Varenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('common.fields.quantity', {}, 'Antall')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('manager.modules.workingOrders.lines.columns.delivered', {}, 'Levert')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('manager.modules.workingOrders.lines.columns.remaining', {}, 'Gjenstående')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('manager.modules.workingOrders.lines.columns.unit', {}, 'Enhet')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">{t('common.fields.status', {}, 'Status')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">{t('common.fields.action', {}, 'Handling')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {lines.map((line) => {
                const isCancelled = line.lineStatus === 80;
                const canCancel = !isCancelled && (line.quantityDelivered ?? 0) === 0;
                return (
                  <tr key={line.id} className={`transition-colors ${isCancelled ? 'bg-slate-100 text-slate-400' : 'hover:bg-slate-100'}`}>
                    <td className={`py-2 px-3 font-medium ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{line.lineNo}</td>
                    <td className={`py-2 px-3 font-mono ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{line.itemNo || '-'}</td>
                    <td className={`py-2 px-3 ${isCancelled ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{line.description1 || '-'}</td>
                    <td className={`py-2 px-3 text-right ${isCancelled ? 'text-slate-400' : 'text-slate-900'}`}>{line.quantity?.toFixed(2) ?? '-'}</td>
                    <td className={`py-2 px-3 text-right ${isCancelled ? 'text-slate-400' : 'text-slate-900'}`}>{line.quantityDelivered?.toFixed(2) ?? '-'}</td>
                    <td className={`py-2 px-3 text-right font-medium ${isCancelled ? 'text-slate-400' : 'text-slate-900'}`}>{isCancelled ? '-' : (line.remainingQuantity?.toFixed(2) ?? '-')}</td>
                    <td className={`py-2 px-3 ${isCancelled ? 'text-slate-400' : 'text-slate-900'}`}>{line.itemUnitCode || '-'}</td>
                    <td className={`py-2 px-3 ${isCancelled ? 'text-slate-400' : 'text-slate-900'}`}>{line.warehouse || '-'}</td>
                    <td className="py-2 px-3">
                      {isCancelled ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">
                          {t('manager.modules.workingOrders.lines.cancelled', {}, 'Kansellert')}
                        </span>
                      ) : (
                        <span className="text-slate-900">{line.statusText || '-'}</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {isCancelled ? (
                        <button
                          onClick={() => handleReopenLine(order.orderNo, line.lineNo)}
                          className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                        >
                          {t('manager.modules.workingOrders.lines.reopen', {}, 'Gjenåpne')}
                        </button>
                      ) : canCancel ? (
                        <button
                          onClick={() => handleCancelLine(order.orderNo, line.lineNo)}
                          className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                        >
                          {t('manager.modules.workingOrders.lines.cancel', {}, 'Kanseller')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-4 text-gray-500 text-sm">
            {t('manager.modules.workingOrders.lines.noLines', {}, 'Ingen ordrelinjer funnet for denne bestillingen')}
          </div>
        )}
      </div>
    );
  }, [orderLines, loadingLines, t, handleCancelLine, handleReopenLine]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<WorkingOrderTableRow, null> = useMemo(() => ({
    getDetailData: () => null,
    renderDetail: renderOrderLines,
    expandOnRowClick: false,
    onExpand: (row) => {
      loadOrderLines(row.orderNo);
    }
  }), [renderOrderLines, loadOrderLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: loadOrders,
      variant: 'secondary' as const
    }
  ], [loadOrders, t]);

  // Derived: deletable orders from selection (status < 20)
  const deletableOrders = useMemo(() =>
    selectedOrderData.filter(o => o.orderStatus < 20),
    [selectedOrderData]
  );
  const nonDeletableCount = selectedOrderData.length - deletableOrders.length;
  const canDeleteSelected = deletableOrders.length > 0;
  const canFinalizeSelected = selectedOrderData.length > 0 && selectedOrderData.every(o => o.orderStatus < 60);

  // Toolbar left content - order count + delete button
  const toolbarLeft = useMemo(() => {
    const buttonLabel = deletableOrders.length > 1
      ? t('manager.modules.workingOrders.delete.buttonLabelMulti', { '0': deletableOrders.length }, `Slett ${deletableOrders.length} bestillinger`)
      : t('manager.modules.workingOrders.delete.buttonLabelSingle', {}, 'Slett bestilling');

    const buttonTitle = selectedOrderData.length === 0
      ? t('manager.modules.workingOrders.delete.titleNoneSelected', {}, 'Velg en eller flere bestillinger for å slette')
      : !canDeleteSelected
      ? t('manager.modules.workingOrders.delete.titleNoneDeletable', {}, 'Ingen av de valgte bestillingene kan slettes (krever status Ubehandlet eller Godkjent)')
      : nonDeletableCount > 0
      ? t('manager.modules.workingOrders.delete.titlePartial', { '0': deletableOrders.length, '1': selectedOrderData.length, '2': nonDeletableCount }, `${deletableOrders.length} av ${selectedOrderData.length} valgte kan slettes (${nonDeletableCount} har for høy status)`)
      : deletableOrders.length === 1
      ? t('manager.modules.workingOrders.delete.titleSingle', { '0': deletableOrders[0].orderNoERP }, `Slett bestilling ${deletableOrders[0].orderNoERP}`)
      : t('manager.modules.workingOrders.delete.titleMulti', { '0': deletableOrders.length }, `Slett ${deletableOrders.length} valgte bestillinger`);

    return (
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-600 font-medium">
          {t('manager.modules.workingOrders.ordersForReceiving', { '0': tableData.length }, `${tableData.length} bestillinger for varemottak`)}
          {selectedOrderData.length > 0 && (
            <span className="ml-2 text-blue-600">{t('manager.modules.workingOrders.selectedCount', { '0': selectedOrderData.length }, `(${selectedOrderData.length} valgt)`)}</span>
          )}
        </div>
        <button
          onClick={() => {
            if (deletableOrders.length > 0) {
              setDeleteConfirm({
                orders: deletableOrders.map(o => ({
                  orderNo: o.orderNo,
                  orderNoERP: o.orderNoERP,
                  numOfLines: o.numOfLines
                }))
              });
            }
          }}
          disabled={!canDeleteSelected}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
            canDeleteSelected
              ? 'text-red-700 bg-white border border-red-300 hover:bg-red-50 hover:shadow-md cursor-pointer'
              : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
          }`}
          title={buttonTitle}
        >
          <Trash2 size={14} />
          <span>{buttonLabel}</span>
        </button>
        <button
          onClick={() => {
            if (canFinalizeSelected) {
              setSuccessMessage(null);
              setFinalizeConfirm(selectedOrderData.map(o => ({
                orderNo: o.orderNo,
                orderNoERP: o.orderNoERP,
                orderStatus: o.orderStatus
              })));
            }
          }}
          disabled={!canFinalizeSelected}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all duration-200 ${
            canFinalizeSelected
              ? 'text-blue-700 bg-white border border-blue-300 hover:bg-blue-50 hover:shadow-md cursor-pointer'
              : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
          }`}
        >
          <CheckCheck size={14} />
          <span>{t('manager.modules.workingOrders.finalize.button', {}, 'Avslutt og eksporter kvittering')}</span>
        </button>
        {showDocInterpretation && <button
          onClick={() => setDocInterpretationOrders(selectedOrderData)}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all duration-200 text-blue-700 bg-white border border-blue-300 hover:bg-blue-50 hover:shadow-md cursor-pointer"
        >
          <FileScan size={14} />
          <span>{t('manager.modules.workingOrders.docInterpretation.open', {}, 'Les inn leverandørdokument')}</span>
        </button>}
      </div>
    );
  }, [tableData.length, selectedOrderData, deletableOrders, nonDeletableCount, canDeleteSelected, canFinalizeSelected, showDocInterpretation, t]);

  if (docInterpretationOrders) {
    return (
      <DocInterpretationView
        preselectedOrders={docInterpretationOrders.map(o => ({
          orderNo: o.orderNo,
          orderNoERP: o.orderNoERP,
          supplierName: o.supplierName
        }))}
        onClose={() => {
          setDocInterpretationOrders(null);
          loadOrders();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4 mb-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <PackageOpen size={24} />
            <span>{t('manager.modules.workingOrders.title', {}, 'Administrer bestillinger')}</span>
          </h1>
        </div>
      </div>

      {/* Table content */}
      <div className="flex-1 min-h-0 px-4 py-2">
        {successMessage && (
          <div className="mb-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center justify-between">
            <span className="text-sm text-green-800 flex items-center gap-2">
              <CheckCircle size={16} />
              {successMessage}
            </span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-sm text-green-800 hover:text-green-900 px-3 py-1"
            >
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <Loader className="animate-spin" size={20} />
              <span>{t('manager.modules.workingOrders.loadingOrders', {}, 'Laster bestillinger...')}</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
            <h3 className="text-red-800 font-medium flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {t('manager.modules.workingOrders.errors.loadFailed', {}, 'Feil ved lasting av bestillinger')}
            </h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={loadOrders}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>{t('manager.modules.workingOrders.errors.retry', {}, 'Prøv igjen')}</span>
            </button>
          </div>
        ) : (
          <WarehouseTable
            data={tableData}
            columns={columns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: true,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              stickyFooter: true,
              compactMode: true,
              persistenceKey: 'working-orders-table',
              allowRowSelection: { mode: 'multi', selectAllMode: 'current-page' }
            }}
            summaryCalculations={summaryCalculations}
            getRowId={getRowId}
            onSelectionChange={handleSelectionChange}
            tableActions={tableActions}
            expandableConfig={expandableConfig}
            toolbarLeft={toolbarLeft}
            loading={false}
            error={undefined}
            aria-label={t('manager.modules.workingOrders.tableAriaLabel', {}, 'Administrer bestillinger tabell')}
          />
        )}
      </div>
      {/* Avslutt og eksporter kvittering — bekreftelse */}
      {finalizeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <CheckCheck className="text-blue-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">{t('manager.modules.workingOrders.finalize.confirmTitle', {}, 'Avslutte bestilling?')}</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                {t('manager.modules.workingOrders.finalize.confirmText', { '0': finalizeConfirm.length }, `${finalizeConfirm.length} bestilling(er) avsluttes, og ERP får beskjed om at det ikke kommer mer på dem. Det som ikke er mottatt, lukkes i ERP.`)}
              </p>
              <div className="mt-3 max-h-40 overflow-y-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.orderNoERP', {}, 'ERP Ordrenr')}</th>
                      <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.orderNo', {}, 'Ordrenr')}</th>
                      <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.orderStatus', {}, 'Ordrestatus')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {finalizeConfirm.map(o => (
                      <tr key={o.orderNo}>
                        <td className="py-1.5 px-3 text-gray-900">{o.orderNoERP}</td>
                        <td className="py-1.5 px-3 text-gray-500">{o.orderNo}</td>
                        <td className="py-1.5 px-3"><StatusBadge value={o.orderStatus} statusType={1} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                {t('manager.modules.workingOrders.finalize.confirmNote', {}, 'Har en bestilling mottak som ikke er sendt til ERP ennå, stoppes avslutningen og linjene vises.')}
              </p>
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => setFinalizeConfirm(null)}
                disabled={finalizing}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleFinalizeOrders}
                disabled={finalizing}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {finalizing ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>{t('manager.modules.workingOrders.finalize.finalizing', {}, 'Avslutter...')}</span>
                  </>
                ) : (
                  <>
                    <CheckCheck size={16} />
                    <span>{t('manager.modules.workingOrders.finalize.confirmButton', {}, 'Avslutt og eksporter')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Avslutt og eksporter kvittering — stoppet */}
      {finalizeBlocked && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-orange-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">{t('manager.modules.workingOrders.finalize.blockedTitle', {}, 'Bestillingen kan ikke avsluttes')}</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">{finalizeBlocked.message}</p>
              {finalizeBlocked.lines.length > 0 && (
                <div className="mt-3 max-h-72 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.orderNoERP', {}, 'ERP Ordrenr')}</th>
                        <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.lines.columns.lineNo', {}, 'Linjenr')}</th>
                        <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.lines.columns.itemNo', {}, 'Varenr')}</th>
                        <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600">{t('common.fields.quantity', {}, 'Antall')}</th>
                        <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.finalize.reason', {}, 'Årsak')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {finalizeBlocked.lines.map((l, idx) => (
                        <tr key={`${l.source}-${l.orderNo}-${l.lineNo}-${idx}`}>
                          <td className="py-1.5 px-3 text-gray-900">{l.orderNoERP || l.orderNo}</td>
                          <td className="py-1.5 px-3 text-gray-900">{l.lineNo}</td>
                          <td className="py-1.5 px-3 font-mono text-gray-900">{l.itemNo || '-'}</td>
                          <td className="py-1.5 px-3 text-gray-900">{l.description1 || '-'}</td>
                          <td className="py-1.5 px-3 text-right text-gray-900">{l.quantity}</td>
                          <td className="py-1.5 px-3 text-gray-700">
                            {l.source === 'PutAway'
                              ? t('manager.modules.workingOrders.finalize.reasonPutAway', {}, 'Plassering ikke fullført')
                              : l.lineStatus < 50
                              ? t('manager.modules.workingOrders.finalize.reasonInProgress', {}, 'Mottak pågår')
                              : t('manager.modules.workingOrders.finalize.reasonNotExported', {}, 'Mottatt, ikke eksportert')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => setFinalizeBlocked(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                {t('common.buttons.close', {}, 'Lukk')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-red-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">{t('manager.modules.workingOrders.delete.confirmTitle', {}, 'Bekreft sletting')}</h2>
            </div>
            <div className="p-6">
              {deleteConfirm.orders.length === 1 ? (
                <>
                  <p className="text-gray-700">
                    {t('manager.modules.workingOrders.delete.confirmSinglePrefix', {}, 'Er du sikker på at du vil slette bestilling')} <span className="font-semibold">{deleteConfirm.orders[0].orderNoERP}</span> {t('manager.modules.workingOrders.delete.confirmSingleSuffix', { '0': deleteConfirm.orders[0].orderNo }, `(ordrenr ${deleteConfirm.orders[0].orderNo})?`)}
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    {deleteConfirm.orders[0].numOfLines != null
                      ? t('manager.modules.workingOrders.delete.warningSingleWithLines', { '0': deleteConfirm.orders[0].numOfLines }, `Alle tilhørende bestillingslinjer (${deleteConfirm.orders[0].numOfLines} stk) vil også bli slettet. Denne handlingen kan ikke angres.`)
                      : t('manager.modules.workingOrders.delete.warningNoUndo', {}, 'Alle tilhørende bestillingslinjer vil også bli slettet. Denne handlingen kan ikke angres.')}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-700">
                    {t('manager.modules.workingOrders.delete.confirmMultiPrefix', {}, 'Er du sikker på at du vil slette')} <span className="font-semibold">{t('manager.modules.workingOrders.delete.confirmMultiCount', { '0': deleteConfirm.orders.length }, `${deleteConfirm.orders.length} bestillinger`)}</span>?
                  </p>
                  <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.orderNoERP', {}, 'ERP Ordrenr')}</th>
                          <th className="text-left py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.orderNo', {}, 'Ordrenr')}</th>
                          <th className="text-right py-1.5 px-3 font-medium text-gray-600">{t('manager.modules.workingOrders.columns.numOfLines', {}, 'Ant. linjer')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {deleteConfirm.orders.map(o => (
                          <tr key={o.orderNo}>
                            <td className="py-1.5 px-3 text-gray-900">{o.orderNoERP}</td>
                            <td className="py-1.5 px-3 text-gray-500">{o.orderNo}</td>
                            <td className="py-1.5 px-3 text-gray-500 text-right">{o.numOfLines ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    {t('manager.modules.workingOrders.delete.warningNoUndo', {}, 'Alle tilhørende bestillingslinjer vil også bli slettet. Denne handlingen kan ikke angres.')}
                  </p>
                </>
              )}
              {deleteProgress && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader className="animate-spin" size={14} />
                    <span>{t('manager.modules.workingOrders.delete.progress', { '0': deleteProgress.current, '1': deleteProgress.total }, `Sletter ${deleteProgress.current} av ${deleteProgress.total}...`)}</span>
                  </div>
                  {deleteProgress.failed.length > 0 && (
                    <p className="text-xs text-red-500 mt-1">
                      {t('manager.modules.workingOrders.delete.failedCount', { '0': deleteProgress.failed.length }, `${deleteProgress.failed.length} feilet`)}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleDeleteOrders}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>{t('manager.modules.workingOrders.delete.deleting', {}, 'Sletter...')}</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>{deleteConfirm.orders.length > 1
                      ? t('manager.modules.workingOrders.delete.confirmButtonMulti', { '0': deleteConfirm.orders.length }, `Slett (${deleteConfirm.orders.length})`)
                      : t('manager.modules.workingOrders.delete.confirmButton', {}, 'Slett')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingOrders;
